import React, { useState } from "react";
import { Table, Pagination, Button } from "antd";
import moment from "moment";

import { ContentLoader } from "@components";
import { COUNTRY_FLAGS } from "@config";
import { i18, trb } from "@utilities";
import { CONFIGS } from "@config/Applications";
import useAppConfigControl from "@hooks/useAppConfigControl";
import { CountriesSettingsTableFilter } from "../Util/CountriesSettingsTableFilter";
import { EditCountry } from "../Modals/EditCountry";

import "./CountriesSettingsTab.less";

export const INITIAL_FILTER_VALUES = {
  iso2: "",
  isActual: true,
  page: 1,
  rowsPerPage: 20
};

const getCombinedFlagRow = flags => {
  const combinedFlags = {};
  for (const flag of flags) {
    combinedFlags[flag.riskTitle] = flag.isActive;
  }
  // need to return array in order to loop in ant table
  return [combinedFlags];
};

const renderFlagCell = isActive =>
  isActive
    ? trb(i18.ApplicationsLabels.CountryFlagIsActiveTrue)
    : trb(i18.ApplicationsLabels.CountryFlagIsActiveFalse);

export const CountriesSettingsTab = React.memo(() => {
  const {
    onFilter,
    onPageChange,
    tableFilters: countryFilters,
    config: countriesConfig,
    isLoading,
    downloadExcel,
    isDownloadingExcel
  } = useAppConfigControl({
    initialFilters: INITIAL_FILTER_VALUES,
    configKey: CONFIGS.COUNTRIES
  });

  const [selectedCountry, setSelectedCountry] = useState();

  if (isLoading) {
    return <ContentLoader />;
  }

  const countriesColumn = [
    {
      title: trb(i18.ApplicationsLabels.CountrySettingsTableOs2Col),
      dataIndex: "iso2",
      key: "iso2",
      render: (item, row) => (
        <Button
          type="primary"
          className="country-risk-open-modal-btn"
          onClick={() => setSelectedCountry(row)}
        >
          {item}
        </Button>
      )
    },
    {
      title: trb(i18.ApplicationsLabels.CountrySettingsTableOs3Col),
      dataIndex: "iso3",
      key: "iso3"
    },
    {
      title: trb(i18.ApplicationsLabels.CountrySettingsTableTitleCol),
      dataIndex: "title",
      key: "title"
    },
    {
      title: trb(i18.ApplicationsLabels.CountrySettingsTableRatingCol),
      dataIndex: "rating",
      key: "rating"
    },
    {
      title: trb(i18.ApplicationsLabels.CountrySettingsTableisActualCol),
      dataIndex: "isActual",
      key: "isActual",
      render: item =>
        item
          ? trb(i18.ApplicationsLabels.CountrySettingsTableisActualTrue)
          : trb(i18.ApplicationsLabels.CountrySettingsTableisActualFalse)
    },
    {
      title: trb(i18.ApplicationsLabels.CountrySettingsTableCreatedCol),
      dataIndex: "created",
      key: "created",
      render: item => item && moment(item).format("DD/MM/YYYY HH:mm:ss")
    },
    {
      title: trb(i18.ApplicationsLabels.CountrySettingsTableDeletedCol),
      dataIndex: "deletedDate",
      key: "deletedDate",
      render: item => item && moment(item).format("DD/MM/YYYY HH:mm:ss")
    }
  ];

  const countrieFlagColumn = [
    {
      title: trb(i18.ApplicationsLabels.CountryFlagNoneCol),
      dataIndex: COUNTRY_FLAGS.NONE,
      key: COUNTRY_FLAGS.NONE,
      render: item => renderFlagCell(item)
    },
    {
      title: trb(i18.ApplicationsLabels.CountryFlagEuropeanUnionCol),
      dataIndex: COUNTRY_FLAGS.EUROPEAN_UNION,
      key: COUNTRY_FLAGS.EUROPEAN_UNION,
      render: item => renderFlagCell(item)
    },
    {
      title: trb(i18.ApplicationsLabels.CountryFlagEuroZoneCol),
      dataIndex: COUNTRY_FLAGS.EURO_ZONE,
      key: COUNTRY_FLAGS.EURO_ZONE,
      render: item => renderFlagCell(item)
    },
    {
      title: trb(i18.ApplicationsLabels.CountryFlagEuroEconomicAreaCol),
      dataIndex: COUNTRY_FLAGS.EUROPE_ECONOMIC_AREA,
      key: COUNTRY_FLAGS.EUROPE_ECONOMIC_AREA,
      render: item => renderFlagCell(item)
    },
    {
      title: trb(i18.ApplicationsLabels.CountryFlagHighRiskCol),
      dataIndex: COUNTRY_FLAGS.HIGH_RISK,
      key: COUNTRY_FLAGS.HIGH_RISK,
      render: item => renderFlagCell(item)
    },
    {
      title: trb(i18.ApplicationsLabels.CountryFlagOffShoreCol),
      dataIndex: COUNTRY_FLAGS.OFF_SHORE,
      key: COUNTRY_FLAGS.OFF_SHORE,
      render: item => renderFlagCell(item)
    },
    {
      title: trb(i18.ApplicationsLabels.CountryFlagSanctionedCol),
      dataIndex: COUNTRY_FLAGS.SANCTIONED,
      key: COUNTRY_FLAGS.SANCTIONED,
      render: item => renderFlagCell(item)
    }
  ];

  return (
    <>
      <div className="countries-settings-tab">
        <CountriesSettingsTableFilter
          onFilter={onFilter}
          filterValues={countryFilters}
          handleDownload={downloadExcel}
          isDownloadingExcel={isDownloadingExcel}
        />
        <Table
          className="w-100 mb-3 country-setting-table"
          dataSource={countriesConfig.items}
          columns={countriesColumn}
          scroll={{ x: "60vw" }}
          pagination={false}
          rowKey="id"
          size="small"
          expandable={{
            expandedRowRender: (item, index) => (
              <Table
                dataSource={getCombinedFlagRow(item.countryFlagsLists)}
                className="flag-table"
                columns={countrieFlagColumn}
                pagination={false}
                size="small"
                bordered
              />
            ),
            defaultExpandAllRows: true,
            expandedRowClassName: () => "country-flag-expanded-row",
            showExpandColumn: false
          }}
        />
        <div className="text-right">
          <Pagination
            showSizeChanger
            pageSize={countryFilters.rowsPerPage}
            current={countryFilters.page}
            onChange={onPageChange}
            defaultCurrent={1}
            total={countriesConfig.paging.totalItems}
          />
        </div>
      </div>
      {selectedCountry && (
        <EditCountry
          countryConfig={selectedCountry}
          onClose={() => setSelectedCountry(undefined)}
        />
      )}
    </>
  );
});
